import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { PlusIcon, PushPinIcon, PushPinSlashIcon } from '@phosphor-icons/react';
import { Num, Plural, T, useGT } from 'gt-react';
import type { WorkbookPivotSheet, WorkbookPivotSpec } from '@erp/shared-types';
import { KEY_SEPARATOR } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  addPivotField,
  movePivotField,
  pinPivotColumns,
  removePivotField,
  type PivotArea,
} from './pivotEdits';
import type { PivotField } from './pivotFields';
import {
  FilterCard,
  GroupCard,
  ValueCard,
  type CardFrame,
} from './PivotFieldCards';
import { usePivotLabels } from './usePivotLabels';

type Change = (
  update: (sheet: WorkbookPivotSheet) => WorkbookPivotSheet,
) => void;
/** What is being dragged: a field from the list, or a card from an area. */
type Dragged = { field: string } | { area: PivotArea; index: number };

const AREAS: readonly PivotArea[] = ['rows', 'columns', 'values', 'filters'];

/**
 * The pivot editor's side panel, after a spreadsheet's: its Data, the input's
 * Fields, and the Rows, Columns, Values and Filters areas. Fields are dragged
 * from the list or between areas, or added from each area's field picker.
 */
export function PivotEditor({
  sheet,
  fields,
  inputs,
  observedColumnKeys,
  readOnly,
  change,
}: {
  sheet: WorkbookPivotSheet;
  fields: readonly PivotField[];
  /** The Sources and Worksheets the pivot can summarize. */
  inputs: readonly { id: string; name: string }[];
  /** The Column keys its newest Result spread into, pinned or not. */
  observedColumnKeys?: readonly (readonly string[])[];
  readOnly: boolean;
  change: Change;
}) {
  const gt = useGT();
  const dataLabelId = useId();
  const labels = usePivotLabels();
  const dragged = useRef<Dragged | null>(null);
  const [over, setOver] = useState<PivotArea | null>(null);
  const spec = sheet.pivot;
  const fieldNamed = (name: string) =>
    fields.find((field) => field.name === name);
  const hint = (name: string) => ({
    numeric: fieldNamed(name)?.numeric ?? false,
  });
  const edit = (update: (pivot: WorkbookPivotSpec) => WorkbookPivotSpec) =>
    change((latest) => ({ ...latest, pivot: update(latest.pivot) }));

  function startDrag(event: DragEvent, item: Dragged) {
    dragged.current = item;
    event.dataTransfer?.setData?.(
      'text/plain',
      'field' in item ? item.field : item.area,
    );
  }
  function drop(area: PivotArea) {
    const item = dragged.current;
    dragged.current = null;
    setOver(null);
    if (!item || readOnly) return;
    if ('field' in item)
      edit((pivot) => addPivotField(pivot, area, item.field, hint(item.field)));
    // Back into its own area: there is no drop position to move it to.
    else if (item.area !== area)
      edit((pivot) => {
        const name =
          item.area === 'filters'
            ? pivot.filters[item.index]?.column
            : pivot[item.area][item.index]?.field;
        return movePivotField(pivot, item, { area }, hint(name ?? ''));
      });
  }

  function frame(
    area: PivotArea,
    index: number,
    count: number,
    field: string,
  ): CardFrame {
    return {
      area,
      index,
      count,
      field,
      readOnly,
      onRemove: () => edit((pivot) => removePivotField(pivot, area, index)),
      onMove: (delta) =>
        edit((pivot) =>
          movePivotField(
            pivot,
            { area, index },
            { area, index: index + delta },
            hint(field),
          ),
        ),
      onDragStart: (event) => startDrag(event, { area, index }),
    };
  }

  function cards(area: PivotArea): ReactNode {
    if (area === 'filters')
      return spec.filters.map((filter, index) => (
        <FilterCard
          key={`${filter.column}-${index}`}
          frame={frame(area, index, spec.filters.length, filter.column)}
          filter={filter}
          field={fieldNamed(filter.column)}
          change={(next) =>
            edit((pivot) => ({
              ...pivot,
              filters: pivot.filters.map((item, i) =>
                i === index ? next : item,
              ),
            }))
          }
        />
      ));
    if (area === 'values')
      return spec.values.map((value, index) => (
        <ValueCard
          key={`${value.field}-${index}`}
          frame={frame(area, index, spec.values.length, value.field)}
          value={value}
          change={(next) =>
            edit((pivot) => ({
              ...pivot,
              values: pivot.values.map((item, i) =>
                i === index ? next : item,
              ),
            }))
          }
        />
      ));
    const groups = spec[area];
    return groups.map((group, index) => (
      <GroupCard
        key={`${group.field}-${index}`}
        frame={frame(area, index, groups.length, group.field)}
        group={group}
        spec={spec}
        field={fieldNamed(group.field)}
        innermost={index === groups.length - 1}
        change={(next) =>
          edit((pivot) => ({
            ...pivot,
            [area]: pivot[area].map((item, i) => (i === index ? next : item)),
          }))
        }
      />
    ));
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span id={dataLabelId}>
          <T context="pivot data">Data</T>
        </span>
        <Select
          disabled={readOnly}
          value={sheet.inputId || null}
          onValueChange={(inputId) =>
            change((latest) => ({ ...latest, inputId }))
          }
        >
          <SelectTrigger
            className="w-full text-foreground"
            aria-labelledby={dataLabelId}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {inputs.map((input) => (
              <SelectItem key={input.id} value={input.id}>
                {input.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <section aria-label={gt('Fields', { context: 'pivot editor fields' })}>
        <h3 className="mb-1 text-xs font-medium text-muted-foreground">
          <T context="pivot editor fields">Fields</T>
        </h3>
        {fields.length ? (
          <ul className="flex flex-wrap gap-1">
            {fields.map((field) => (
              <li
                key={field.name}
                draggable={!readOnly}
                onDragStart={(event) => startDrag(event, { field: field.name })}
                className="cursor-grab rounded-md border bg-background px-2 py-0.5 text-xs"
              >
                {field.name}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">
            <T context="pivot editor no fields">
              Run the workbook to load this pivot's fields.
            </T>
          </p>
        )}
      </section>
      {AREAS.map((area) => (
        <section
          key={area}
          aria-label={labels.areas[area]}
          className={`rounded-md border p-2 ${over === area ? 'bg-muted' : 'bg-muted/30'}`}
          onDragOver={(event) => {
            if (readOnly || !dragged.current) return;
            event.preventDefault();
            setOver(area);
          }}
          onDragLeave={() => setOver(null)}
          onDrop={(event) => {
            event.preventDefault();
            drop(area);
          }}
        >
          <div className="mb-1.5 flex items-center gap-2">
            <h3 className="mr-auto text-xs font-medium text-muted-foreground">
              {labels.areas[area]}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  disabled={readOnly || !fields.length}
                  aria-label={gt('Add a field to {area}', {
                    context: 'pivot editor add field',
                    area: labels.areas[area],
                  })}
                >
                  <PlusIcon />
                  {gt('Add', { context: 'pivot editor add field' })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72">
                {fields.map((field) => (
                  <DropdownMenuItem
                    key={field.name}
                    onClick={() =>
                      edit((pivot) =>
                        addPivotField(
                          pivot,
                          area,
                          field.name,
                          hint(field.name),
                        ),
                      )
                    }
                  >
                    {field.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <ul className="space-y-1.5">{cards(area)}</ul>
          {area === 'columns' && spec.columns.length > 0 && (
            <PinnedColumns
              spec={spec}
              observed={observedColumnKeys}
              readOnly={readOnly}
              pin={(keys) => edit((pivot) => pinPivotColumns(pivot, keys))}
            />
          )}
        </section>
      ))}
      <PivotOptions spec={spec} readOnly={readOnly} edit={edit} />
    </div>
  );
}

/**
 * Whether the Column keys come from the data or are pinned. Pinned columns
 * keep their names whatever the data holds, so other Worksheets can read them.
 */
function PinnedColumns({
  spec,
  observed,
  readOnly,
  pin,
}: {
  spec: WorkbookPivotSpec;
  observed?: readonly (readonly string[])[];
  readOnly: boolean;
  pin: (keys: readonly (readonly string[])[] | null) => void;
}) {
  const id = (key: readonly string[]) => key.join(KEY_SEPARATOR);
  if (!spec.columnKeys)
    return (
      <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="mr-auto">
          <T context="pivot columns from data">
            Columns come from the data, so other sheets can't rely on their
            names.
          </T>
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={readOnly || !observed?.length}
          onClick={() => pin(observed ?? [])}
        >
          <PushPinIcon />
          <T context="pivot pin columns">Pin columns</T>
        </Button>
      </div>
    );
  const pinned = new Set(spec.columnKeys.map(id));
  const added = (observed ?? []).filter((key) => !pinned.has(id(key)));
  return (
    <div className="mt-1.5 space-y-1 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span className="mr-auto">
          <T context="pivot pinned columns">
            <Num>{spec.columnKeys.length}</Num>{' '}
            <Plural
              n={spec.columnKeys.length}
              one="pinned column"
              other="pinned columns"
            />
          </T>
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={readOnly}
          onClick={() => pin(null)}
        >
          <PushPinSlashIcon />
          <T context="pivot unpin columns">Unpin</T>
        </Button>
      </div>
      {added.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="mr-auto" title={added.map(id).join(', ')}>
            <T context="pivot new column values">
              <Num>{added.length}</Num>{' '}
              <Plural
                n={added.length}
                one="new value in the data isn't shown"
                other="new values in the data aren't shown"
              />
            </T>
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={readOnly}
            onClick={() => pin([...(spec.columnKeys ?? []), ...added])}
          >
            <T context="pivot add new columns">Add them</T>
          </Button>
        </div>
      )}
    </div>
  );
}

type OptionUpdate = (
  checked: boolean,
) => (pivot: WorkbookPivotSpec) => WorkbookPivotSpec;
const showEmptyGroups: OptionUpdate = (show) => (pivot) => ({
  ...pivot,
  emptyGroups: show ? 'show' : 'hide',
});
const showZeroCells: OptionUpdate = (zero) => (pivot) => ({
  ...pivot,
  emptyCells: zero ? 'zero' : 'empty',
});

function PivotOptions({
  spec,
  readOnly,
  edit,
}: {
  spec: WorkbookPivotSpec;
  readOnly: boolean;
  edit: (update: (pivot: WorkbookPivotSpec) => WorkbookPivotSpec) => void;
}) {
  const option = (
    checked: boolean,
    label: ReactNode,
    update: (
      checked: boolean,
    ) => (pivot: WorkbookPivotSpec) => WorkbookPivotSpec,
  ) => (
    <label className="flex items-center gap-2 text-xs">
      <Checkbox
        disabled={readOnly}
        checked={checked}
        onCheckedChange={(next) => edit(update(next))}
      />
      {label}
    </label>
  );
  return (
    <section className="space-y-1">
      <h3 className="text-xs font-medium text-muted-foreground">
        <T context="pivot editor options">Options</T>
      </h3>
      {option(
        spec.grandTotals.rows,
        <T context="pivot option">Grand total row</T>,
        (rows) => (pivot) => ({
          ...pivot,
          grandTotals: { ...pivot.grandTotals, rows },
        }),
      )}
      {option(
        spec.grandTotals.columns,
        <T context="pivot option">Grand total columns</T>,
        (columns) => (pivot) => ({
          ...pivot,
          grandTotals: { ...pivot.grandTotals, columns },
        }),
      )}
      {option(
        spec.emptyGroups === 'show',
        <T context="pivot option">Show (Empty) groups</T>,
        showEmptyGroups,
      )}
      {option(
        spec.emptyCells === 'zero',
        <T context="pivot option">Show 0 in cells with no rows</T>,
        showZeroCells,
      )}
    </section>
  );
}
